import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import moment from "moment";


interface IShipmentsContext {
    recordCount: number
    search: ISearch
    shipments: IShipment[]
    isLoading: boolean
    selectedFilter: string
    anchorEl: Element | undefined

    doSearch: (search: ISearch) => void
    handleChangePage: (event: any, page: number) => void
    updateSearch: (newSearch: ISearch) => void
    searchShipments: (searchRequest: ISearch) => Promise<void>
    openEdit: (filterName: string, event: any) => void
    closeEdit: () => void
}

export let shipmentsContext = React.createContext<IShipmentsContext>({} as IShipmentsContext);

let { Provider } = shipmentsContext;

interface IShipment {
    id: string
    shipmentId: string
    shipmentDate: moment.Moment
    shipmentLines: number
    trackingUrl: string
    trackingNumber: string
    carrierCode: string
}

export interface ISearch {
    fullText: string
    hasTrackingNumber: 'All' | 'WithoutTracking' | 'WithTracking'

    pageNr: number
    pageSize: number
}

export let ShipmentsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { enqueueSnackbar } = useSnackbar();
    let location = useLocation();
    let navigate = useNavigate();

    let [isLoading, setLoading] = useState(true);

    let [shipments, setShipments] = useState<IShipment[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [selectedFilter, setSelectedFilter] = useState<string>('');
    let [anchorEl, setAnchorEl] = useState<Element>();

    let [search, setSearch] = useState<ISearch>({
        fullText: '',
        hasTrackingNumber: 'All',
        pageNr: 0,
        pageSize: 20
    });

    useEffect(() => {
        if (location.state)
            processState(location.state);

    }, [location.state])


    if (!location.state)
        return <Navigate to={location.pathname} state={{ search: search }} replace={true} />


    let processState = (state: any) => {
        if (state) {

            setSearch({
                ...search,
                ...state
            });

            searchShipments({
                ...search,
                ...state
            });
        }
    }

    let doSearch = (values: ISearch) => {
        let request = {
            ...values,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        navigate(location.pathname, { state: request });
    }

    let updateSearch = (newSearch: ISearch) => {
        setSearch(newSearch);
    }

    let searchShipments = async (searchRequest: ISearch) => {
        try {
            setLoading(true);
            let response = await axios.post('/api/Shipment/SearchShipments', searchRequest);

            setShipments(response.data.items);
            setTotalRecords(response.data.recordCount);
            setSearch({ ...searchRequest });

        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            pageNr: page
        }

        window.scrollTo(0, 0);
        navigate(location.pathname, { state: request });
    }

    let openEdit = (filterName: string, event: any) => {
        setAnchorEl(event.currentTarget);
        setSelectedFilter(filterName);
    }

    let closeEdit = () => {
        setAnchorEl(undefined);
        setSelectedFilter('');
    }

    return (
        <Provider value={{
            recordCount,
            search,
            shipments,
            isLoading,
            selectedFilter,
            anchorEl,

            doSearch,
            handleChangePage,
            updateSearch,
            searchShipments,
            openEdit,
            closeEdit
        }}>
            {children}
        </Provider>
    );
};